import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiWithAuth } from '../lib/api';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  ClockIcon,
  CalendarDaysIcon,
  MapPinIcon,
  CurrencyYenIcon,
  BuildingOfficeIcon,
  UserIcon,
  StarIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { API_BASE } from '../lib/config';

const API = `${API_BASE}`;

const WorkHistoryPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [workHistory, setWorkHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, completed, past
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  useEffect(() => {
    fetchWorkHistory();
  }, []);

  const fetchWorkHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const authApi = apiWithAuth(token);
      const response = await authApi.get('/applications');
      
      // 完了済みまたは過去の勤務をフィルタ
      const pastWork = response.data.filter(app => {
        const isCompleted = app.status === 'completed';
        const isPastDate = app.work_date && new Date(app.work_date) < new Date();
        return isCompleted || (app.status === 'accepted' && isPastDate);
      });
      
      // 勤務日の新しい順にソート
      pastWork.sort((a, b) => new Date(b.work_date) - new Date(a.work_date));
      
      setWorkHistory(pastWork);
    } catch (error) {
      console.error('Failed to fetch work history:', error);
      toast.error('勤務履歴の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const isFutureOrToday = (dateString) => {
    if (!dateString) return false;
    const d = new Date(dateString);
    const today = new Date();
    // normalize date-only compare
    const dd = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const tt = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return dd.getTime() >= tt.getTime();
  };

  const resolveTimesheet = async (jobId, workDate) => {
    const url = new URL(`${API}/timesheets/resolve`);
    url.searchParams.set('job_id', jobId);
    if (workDate) url.searchParams.set('work_date', workDate);
    const resp = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
    if (!resp.ok) throw new Error('resolve failed');
    return await resp.json(); // { id, work_date }
  };

  const goAdjust = async (jobId, workDate) => {
    try {
      const ts = await resolveTimesheet(jobId, workDate);
      navigate(`/work/history/${encodeURIComponent(ts.id)}/adjust`);
    } catch (e) {
      console.error(e);
      toast.error('差異申請対象のタイムシートが見つかりません');
    }
  };

  const cancelWork = async (jobId, workDate) => {
    try {
      const ts = await resolveTimesheet(jobId, workDate);
      const reason = window.prompt('キャンセル理由（任意）を入力してください') || '';
      const url = new URL(`${API}/timesheets/${ts.id}/cancel`);
      if (reason) url.searchParams.set('reason', reason);
      const resp = await fetch(url.toString(), { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error('cancel failed');
      toast.success('キャンセルしました');
      fetchWorkHistory();
    } catch (e) {
      console.error(e);
      toast.error('キャンセルに失敗しました');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    return timeString.slice(0, 5); // HH:MM format
  };

  const getStatusBadge = (work) => {
    if (work.status === 'completed') {
      return (
        <span className="bg-green-100 text-green-800 text-xs font-medium px-3 py-1 rounded-full flex items-center">
          <CheckCircleIcon className="h-3 w-3 mr-1" />
          完了
        </span>
      );
    } else if (work.work_date && new Date(work.work_date) < new Date()) {
      return (
        <span className="bg-gray-100 text-gray-800 text-xs font-medium px-3 py-1 rounded-full">
          勤務終了
        </span>
      );
    }
    return null;
  };

  const calculateWorkHours = (startTime, endTime) => {
    if (!startTime || !endTime) return null;
    
    const start = new Date(`2000-01-01T${startTime}`);
    const end = new Date(`2000-01-01T${endTime}`);
    const diffMs = end - start;
    const diffHours = diffMs / (1000 * 60 * 60);
    
    return diffHours;
  };

  const calculateEstimatedPay = (hours, hourlyRate) => {
    if (!hours || !hourlyRate) return null;
    return Math.floor(hours * hourlyRate);
  };

  const filteredHistory = workHistory.filter(work => {
    if (filter === 'completed') return work.status === 'completed';
    if (filter === 'past') return work.status === 'accepted' && work.work_date && new Date(work.work_date) < new Date();
    return true; // all
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">履歴を読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-md mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeftIcon className="h-6 w-6 mr-2" />
              戻る
            </button>
            <h1 className="text-xl font-bold text-gray-900">これまでの仕事</h1>
            <div className="w-8"></div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-md mx-auto px-4 py-4">
        <div className="flex space-x-2">
          {[
            { key: 'all', label: 'すべて' },
            { key: 'completed', label: '完了済み' },
            { key: 'past', label: '勤務終了' }
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === key
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-md mx-auto px-4 pb-6">
        {/* Summary Stats */}
        {workHistory.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">活動統計</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-blue-600">{workHistory.length}</div>
                <div className="text-xs text-gray-600">総お仕事数</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">
                  {workHistory.filter(w => w.status === 'completed').length}
                </div>
                <div className="text-xs text-gray-600">完了数</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">
                  {workHistory.reduce((total, work) => {
                    const hours = calculateWorkHours(work.preferred_work_start_time, work.preferred_work_end_time);
                    return total + (hours || 0);
                  }, 0).toFixed(1)}h
                </div>
                <div className="text-xs text-gray-600">総勤務時間</div>
              </div>
            </div>
          </div>
        )}

        {filteredHistory.length === 0 ? (
          <div className="text-center py-12">
            <ClockIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filter === 'completed' ? '完了したお仕事はありません' : 
               filter === 'past' ? '勤務終了したお仕事はありません' : 
               'お仕事の履歴はありません'}
            </h3>
            <p className="text-gray-600 mb-6">
              マッチング成立後、勤務を行うと履歴がここに表示されます
            </p>
            <Link
              to="/jobs"
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              お仕事を探す
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredHistory.map((work) => {
              const workHours = calculateWorkHours(work.preferred_work_start_time, work.preferred_work_end_time);
              const estimatedPay = calculateEstimatedPay(workHours, work.hourly_rate);
              
              return (
                <div key={work.id} className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
                  {/* Status and Date */}
                  <div className="flex items-center justify-between mb-4">
                    {getStatusBadge(work)}
                    <span className="text-sm text-gray-500">
                      {formatDate(work.work_date)}
                    </span>
                  </div>

                  {/* Job Info */}
                  <div className="mb-4">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {work.job_title || '求人タイトル'}
                    </h3>
                    <div className="flex items-center text-gray-600 mb-2">
                      <BuildingOfficeIcon className="h-4 w-4 mr-2" />
                      <span className="text-sm">{work.business_name}</span>
                    </div>
                    {work.employer_name && (
                      <div className="flex items-center text-gray-600">
                        <UserIcon className="h-4 w-4 mr-2" />
                        <span className="text-sm">{work.employer_name}</span>
                      </div>
                    )}
                  </div>

                  {/* Work Details */}
                  <div className="space-y-3 mb-4">
                    {/* Work Time */}
                    {work.preferred_work_start_time && work.preferred_work_end_time && (
                      <div className="flex items-center text-gray-700">
                        <ClockIcon className="h-5 w-5 mr-3 text-blue-500" />
                        <div>
                          <div className="font-medium">
                            {formatTime(work.preferred_work_start_time)} - {formatTime(work.preferred_work_end_time)}
                          </div>
                          {workHours && (
                            <div className="text-sm text-gray-500">{workHours}時間勤務</div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Pay Information */}
                    {work.hourly_rate && (
                      <div className="flex items-center text-gray-700">
                        <CurrencyYenIcon className="h-5 w-5 mr-3 text-green-500" />
                        <div>
                          <div className="font-medium text-green-600">
                            時給 {work.hourly_rate.toLocaleString()}円
                          </div>
                          {estimatedPay && (
                            <div className="text-sm text-gray-500">
                              推定報酬: {estimatedPay.toLocaleString()}円
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Location */}
                    {work.work_location && (
                      <div className="flex items-center text-gray-700">
                        <MapPinIcon className="h-5 w-5 mr-3 text-red-500" />
                        <div className="font-medium">{work.work_location}</div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3">
                    {/* レビュー: 勤務終了または完了のもの */}
                    {(work.work_date && !isFutureOrToday(work.work_date)) && (
                      <button
                        onClick={() => {
                          const targetId = work.employer_id || work.job_id;
                          navigate(`/reviews/new?target_id=${encodeURIComponent(targetId)}&target_role=employer`);
                        }}
                        className="px-4 py-2 bg-yellow-600 text-white rounded-lg text-sm font-medium hover:bg-yellow-700 transition-colors"
                      >
                        レビューを書く
                      </button>
                    )}
                    {work.status === 'completed' && (
                      <button
                        className="flex items-center justify-center px-4 py-2 bg-yellow-50 text-yellow-700 rounded-lg text-sm font-medium hover:bg-yellow-100 transition-colors"
                        disabled
                      >
                        <StarIcon className="h-4 w-4 mr-1" />
                        評価済み
                      </button>
                    )}
                    {work.job_id && (
                      <Link
                        to={`/jobs/${work.job_id}`}
                        className="flex-1 bg-gray-100 text-gray-700 py-2 px-4 rounded-lg text-sm font-medium text-center hover:bg-gray-200 transition-colors"
                      >
                        詳細確認
                      </Link>
                    )}
                    {/* 差異申請: 勤務終了または完了のもの */}
                    {(work.work_date && !isFutureOrToday(work.work_date)) && (
                      <button
                        onClick={() => goAdjust(work.job_id, work.work_date)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                      >
                        差異申請
                      </button>
                    )}
                    {/* キャンセル: 受諾済み かつ 今日以降 */}
                    {(work.status === 'accepted' && isFutureOrToday(work.work_date)) && (
                      <button
                        onClick={() => cancelWork(work.job_id, work.work_date)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
                      >
                        キャンセル
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Tips */}
        {filteredHistory.length > 0 && (
          <div className="mt-6 bg-purple-50 rounded-xl p-4 border border-purple-100">
            <div className="flex items-start">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center mr-3 mt-1">
                <span className="text-purple-600 text-sm"></span>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-purple-900 mb-1">お疲れさまでした！</h4>
                <p className="text-sm text-purple-700">
                  お仕事の履歴は確定申告の際にも役立ちます。記録をしっかり残しておきましょう。
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkHistoryPage;